<template>
  <div>
    <PageAllow controller="Orders" action="getPricingMaterials" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              deleteIcon
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">
                {{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}
              </v-icon>
            </v-btn>
            <v-col>
              <!-- 批量核价导出 -->
              <v-btn
                small
                color="print"
                dark
                class="ms-1 float-end"
                @click="orderPricingTemplateExport()"
                v-if="
                  $permission.permissionAllow(
                    'Orders',
                    'orderPricingTemplateExport'
                  )
                "
              >
                <v-icon x-small class="pe-1">fa-file-export</v-icon>
                {{ $t("OrdersPricing.export") }}
              </v-btn>
              <!-- 批量核价导入 -->
              <v-btn
                small
                color="print"
                dark
                class="ms-1 float-end"
                @click.stop="ImportDialog = true"
                v-if="
                  $permission.permissionAllow('Orders', 'orderPricingImport')
                "
              >
                <v-icon x-small class="pe-1">fa-file-import</v-icon>
                {{ $t("OrdersPricing.import") }}
              </v-btn>
              <!-- 改价记录 -->
              <v-btn
                small
                dark
                color="primary"
                class="ms-1 float-end"
                href="/#/orders-pricing/logs"
              >
                {{ $t("OrdersPricing.pricingLog") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterialCategory v-model="categorySelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterials
                  v-model="materialSelected"
                  :materialCategories="
                    categorySelected ? [categorySelected] : []
                  "
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStock v-model="stockSelected" showVirtualStock />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherDistrict v-model="districtSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherShops v-model="shopsSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4"> </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.is_template_edit"
                  :items="[
                    {
                      text: $t('OrdersPricing.is_template_edit'),
                      color: 'primary',
                      textColor: 'white',
                      value: 1,
                    },
                  ]"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="materials"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:item.Description="{ item }">
            <a
              class="text-no-wrap"
              :class="item.error_flg ? 'red--text' : ''"
              @click.stop="
                currentDialog = true;
                currentItem = item;
              "
            >
              {{ item.material_name }}
            </a>
            <br />
            <span v-if="item.standard_name" class="caption secondary--text">
              {{ $t("Material.Fields.standard") }}{{ $t("Global.colon")}}
              <span v-if="item.standard_name && item.standard_name.length > 12" :title="item.standard_name" style="cursor: pointer">
                {{ item.standard_name.slice(0, 12) + "......" }}
              </span>
              <span v-else>
                {{ item.standard_name }}
              </span>
            </span>
          </template>
          <template v-slot:item.total_fix_num="{ item }">
            {{ item.total_fix_num | formatTwoDot }}
          </template>
        </v-data-table>
      </div>
    </v-sheet>
    <OrderListsDialog
      v-model="currentDialog"
      :searcherData="options"
      :currentItem="currentItem"
    />
    <ImportExcelAlert
      v-model="ImportDialog"
      :objName="$t('OrdersPricing.ObjectName')"
      hideDownload
      @import="
        excelFile = $event;
        importExcel();
      "
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Orders from "@/api/orders";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherMaterialCategory from "@/components/plugins/SearcherMaterialCategory";
import SearcherMaterials from "@/components/plugins/SearcherMaterials";
import SearcherShops from "@/components/plugins/SearcherShops";
import SearcherStock from "@/components/plugins/SearcherStock";
import SearcherDistrict from "@/components/plugins/SearcherDistrict";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import OrderListsDialog from "./plugins/OrderListsDialog";
import ImportExcelAlert from "@/components/plugins/ImportExcelAlert";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime()).toISOString().substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  actual_time_from: "",
  actual_time_to: "",
  expect_time_from: "",
  expect_time_to: "",
  category_code: "",
  material_ids: [],
  stock_ids: [],
  shop_ids: [],
  district_codes: [],
  is_template_edit: [],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherMaterialCategory,
    SearcherMaterials,
    SearcherShops,
    SearcherStock,
    SearcherDistrict,
    SearcherStatus,
    OrderListsDialog,
    ImportExcelAlert,
  },
  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.orderManagement"),
        disabled: false,
        replace: true,
        href: "/orders",
      },
      {
        text: i18n.t("Nav.ordersPricing"),
        disabled: true,
        href: "/orders-pricing",
      },
    ],
    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("Order.Fields.created_time"),
      },
      {
        key: "actual",
        text: i18n.t("Order.Fields.actual_time"),
      },
      {
        key: "expect",
        text: i18n.t("Order.Fields.expect_date"),
      },
    ],
    headers() {
      return [
        {
          text: i18n.t("OrdersPricing.Fields.material_code"),
          align: "start",
          sortable: false,
          value: "material_code",
          width: 300,
        },
        {
          text: i18n.t("OrdersPricing.Fields.material_name"),
          align: "start",
          sortable: false,
          value: "Description",
          width: 380,
        },
        {
          text: i18n.t("OrdersPricing.Fields.category"),
          align: "start",
          sortable: false,
          value: "category_name",
        },
        {
          text: i18n.t("OrdersPricing.Fields.total_fix_num"),
          align: "start",
          sortable: false,
          value: "total_fix_num",
        },
        {
          text: i18n.t("OrdersPricing.Fields.unit"),
          align: "start",
          sortable: false,
          value: "unit",
        },
      ];
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },
  data: () => ({
    options: JSON.parse(
      sessionStorage.getItem("orders-pricing-options")
        ? sessionStorage.getItem("orders-pricing-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    materials: [],
    categorySelected: null,
    materialSelected: [],
    shopsSelected: [],
    stockSelected: null,
    districtSelected: null,
    currentDialog: false,
    currentItem: {},
    ImportDialog: false,
    excelFile: null,
  }),

  mounted() {
    this.$commonUtils
      .loadOptions("orders-pricing", [
        "materialSelected",
        "categorySelected",
        "shopsSelected",
        "stockSelected",
        "districtSelected",
      ])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "materialSelected":
                this.materialSelected = selected.value;
                break;
              case "categorySelected":
                this.categorySelected = selected.value;
                break;
              case "shopsSelected":
                this.shopsSelected = selected.value;
                break;
              case "stockSelected":
                this.stockSelected = selected.value;
                break;
              case "districtSelected":
                this.districtSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  methods: {
    resetSearch() {
      this.categorySelected = null;
      this.materialSelected = [];
      this.shopsSelected = [];
      this.stockSelected = null;
      this.districtSelected = null;
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {});
    },
    getPricingMaterials() {
      Orders.getPricingMaterials(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.materials = response.data.materials;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    orderPricingTemplateExport() {
      Orders.orderPricingTemplateExport(this.options);
    },
    importExcel() {
      this.$store.dispatch("fullscreenLoading");
      Orders.orderPricingImport(this.excelFile)
        .then(
          function (response) {
            if (response.data.status.isOK()) {
              this.ImportDialog = false;
              this.$snackbar.info(
                i18n.t("Import.success", [i18n.t("OrdersPricing.ObjectName")])
              );
            }
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        )
        .catch(
          function () {
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        );
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getPricingMaterials();
        sessionStorage.setItem(
          "orders-pricing-options",
          JSON.stringify(this.options)
        );
      },
    },
    categorySelected: {
      handler() {
        this.options.category_code = this.categorySelected
          ? this.categorySelected.code
          : null;
        sessionStorage.setItem(
          "orders-pricing-categorySelected",
          JSON.stringify(this.categorySelected)
        );
      },
    },
    materialSelected: {
      handler() {
        this.options.material_ids = this.materialSelected
          ? this.materialSelected.map((e) => {
              return e.id;
            })
          : [];
        sessionStorage.setItem(
          "orders-pricing-materialSelected",
          JSON.stringify(this.materialSelected)
        );
      },
    },
    stockSelected: {
      handler() {
        this.options.stock_ids = this.stockSelected
          ? [this.stockSelected.id]
          : null;
        sessionStorage.setItem(
          "orders-pricing-stockSelected",
          JSON.stringify(this.stockSelected)
        );
      },
    },
    shopsSelected: {
      handler() {
        this.options.shop_ids = this.shopsSelected
          ? this.shopsSelected.map((alliance) => {
              return alliance.id;
            })
          : [];
        sessionStorage.setItem(
          "orders-pricing-shopsSelected",
          JSON.stringify(this.shopsSelected)
        );
      },
    },
    districtSelected: {
      handler() {
        this.options.district_codes = this.districtSelected
          ? [this.districtSelected.code]
          : [];
        sessionStorage.setItem(
          "orders-pricing-districtSelected",
          JSON.stringify(this.districtSelected)
        );
      },
    },
  },
};
</script>